<template>
  <div>
    <!-- Add Button -->
    <v-btn
      small
      color="success"
      @click="
        (dedicationNewCommunicationDialog = true),
          (newComm.commDate = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000
          )
            .toISOString()
            .slice(0, 16))
      "
    >
      New Communication
    </v-btn>

    <!-- Empty State -->
    <v-card-text
      v-if="!communications || communications.length === 0"
      class="text-center title mt-5"
    >
      No Communications
    </v-card-text>

    <!-- Communications List -->
    <v-expansion-panel v-else class="mt-4">
      <v-expansion-panel-content
        style="background-color: #e3f2dc;"
        v-for="comm in communications"
        :key="comm.commID"
      >
        <!-- Header -->
        <template v-slot:header>
          <v-layout row align-center>
            <v-flex xs1>
              <v-icon color="success">{{ getIcon(comm.commType) }}</v-icon>
            </v-flex>

            <v-flex xs5>
              <div class="grey--text body-2 font-weight-medium">
                {{ formatDate(comm.commDate) }}
              </div>
            </v-flex>

            <v-flex xs5>
              <div class="body-2 font-weight-medium">Subject:</div>
              <div class="body-2">
                {{ comm.subject }}
              </div>
            </v-flex>

            <v-flex xs2>
              <div class="body-2 text-capitalize">{{ comm.commType }}</div>
            </v-flex>

            <v-flex xs3>
              <div class="body-2 font-weight-medium">Created By</div>
              <div class="body-2">{{ comm.createdBy }}</div>
            </v-flex>

            <v-flex xs1 class="text-right">
              <div class="body-2 font-weight-medium">
                Sale
                <span class="subheading font-weight-bold primary--text">
                  #{{ comm.saleID }}
                </span>
              </div>
            </v-flex>
          </v-layout>
        </template>

        <!-- Expanded Content -->
        <dedication-communication-view
          :initial-data="{ comm: comm, sales: sales }"
        />
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-dialog
      v-model="dedicationNewCommunicationDialog"
      max-width="800"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="headline">
          Add New Communication
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid">
            <div class="row">
              <!-- Date -->
              <v-flex cols="12" sm="6">
                <v-text-field
                  v-model="newComm.commDate"
                  label="Date & Time"
                  type="datetime-local"
                  prepend-icon="mdi-calendar-clock"
                  :rules="[(v) => !!v || 'Date and time are required']"
                ></v-text-field>
              </v-flex>

              <!-- Type -->
              <v-flex cols="12" sm="6">
                <v-select
                  v-model="newComm.commType"
                  :items="commTypes"
                  label="Communication Type"
                  prepend-icon="mdi-message-text-outline"
                  :rules="[(v) => !!v || 'Type is required']"
                ></v-select>
              </v-flex>

              <!-- Subject -->
              <v-flex cols="12">
                <v-text-field
                  v-model="newComm.subject"
                  label="Subject"
                  prepend-icon="mdi-format-title"
                  :rules="[(v) => !!v || 'Subject is required']"
                ></v-text-field>
              </v-flex>

              <!-- Description -->
              <v-flex cols="12">
                <v-textarea
                  v-model="newComm.description"
                  label="Description"
                  prepend-icon="mdi-text"
                  rows="3"
                  auto-grow
                ></v-textarea>
              </v-flex>

              <!-- Sale ID -->
              <v-select
                v-model="newComm.saleID"
                :items="
                  Array.isArray(initialData.sales) ? initialData.sales : []
                "
                :item-text="
                  (sale) =>
                    `#${sale.saleID} - ${sale.itemName} - ${
                      sale.itemType
                    } - ${sale.offerPrice.toLocaleString()}$`
                "
                item-value="saleID"
                label="Related Sale"
                prepend-icon="mdi-cart"
                clearable
              />
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="mb-3">
          <v-spacer></v-spacer>
          <v-btn dark color="grey" text @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :disabled="!valid"
            :loading="loading"
            @click="saveCommunication"
          >
            Save Communication
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DedicationCommunicationView from "./DedicationCommunicationView.vue";
export default {
  components: {
    DedicationCommunicationView,
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      communications: [],
      sales: [],
      dedicationNewCommunicationDialog: false,
      valid: true,
      loading: false,
      commTypes: [
        "email",
        "phone",
        "meeting",
        "letter",
        "note",
        "sms",
        "whatsapp",
      ],

      newComm: {
        commDate: "",
        commType: "",
        subject: "",
        description: "",
        saleID: null,
      },
    };
  },

  computed: {
    formattedDate() {
      if (!this.newComm.commDate) return "";
      const [year, month, day] = this.newComm.commDate.split("-");
      return `${day}/${month}/${year}`;
    },
    commCount() {
      return this.communications.length;
    },
  },
  async created() {
    await this.loadCommunications();
    this.sales = this.initialData.sales;
  },
  methods: {
    async loadCommunications() {
      try {
        const response = await this.$http.get(
          "api/DonorCommunication/GetAllCommunicationsByPersonID",
          {
            params: { personID: this.$route.params.id },
          }
        );

        // Some setups return response.data, others return array directly
        this.communications = response.data || response;
      } catch (error) {
        console.error("Error loading communications:", error);
      } finally {
        this.$emit("update-comm-count", this.communications.length);
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString();
    },

    getIcon(type) {
      const result = {
        email: "mdi-email",
        phone: "mdi-phone",
        sms: "mdi-message-processing",
        meeting: "mdi-account-multiple",
        letter: "mdi-note-text",
        note: "mdi-note",
        whatsapp: "mdi-whatsapp",
      };
      if (!result[type]) return "mdi-text";
      return result[type];
    },
    closeDialog() {
      this.dedicationNewCommunicationDialog = false;
      this.$nextTick(() => {
        this.resetForm();
      });
    },

    resetForm() {
      this.newComm = {
        commDate: "",
        commType: "",
        subject: "",
        description: "",
        saleID: null,
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    async saveCommunication() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const payload = {
          personID: this.$route.params.id,
          commDate: this.newComm.commDate,
          commType: this.newComm.commType,
          subject: this.newComm.subject,
          description: this.newComm.description || null,
          saleID: this.newComm.saleID || null,
        };
        await this.$http.post("api/DonorCommunication/save", payload);

        await this.loadCommunications();

        this.$notify({
          group: "notification",
          type: "success",
          text: "added successfully!",
        });
        this.closeDialog();
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
